import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { StripeService } from '@/services/stripe.service';
import { useCartContext } from '@/contexts/CartContext';
import { useUser } from '@/contexts/UserContext';

interface PaymentSuccessProps {
    className?: string;
}

export function PaymentSuccess({ className }: PaymentSuccessProps) {
    const [, setLocation] = useLocation();
    const { closeCart } = useCartContext();
    const { isLoggedIn, user } = useUser();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('');
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
    const [pendingCartClose, setPendingCartClose] = useState(false);

    useEffect(() => {
        const checkPaymentStatus = async () => {
            try {
                // Verificar se há session_id na URL (checkout)
                const urlParams = new URLSearchParams(window.location.search);
                const sessionIdParam = urlParams.get('session_id');
                const paymentIntentParam = urlParams.get('payment_intent');

                if (sessionIdParam) {
                    setSessionId(sessionIdParam);
                    const session = await StripeService.getCheckoutSession(sessionIdParam);

                    if (session.success && session.data.payment_status === 'paid') {
                        setStatus('success');
                        setMessage('Pagamento realizado com sucesso!');

                        // Confirmar pagamento no backend (fallback quando webhook não é chamado)
                        console.log('🔔 [PaymentSuccess] Pagamento confirmado no Stripe, confirmando no backend...');
                        try {
                            await StripeService.confirmCheckoutSession(sessionIdParam);
                            console.log('✅ [PaymentSuccess] Pagamento confirmado no backend com sucesso');
                        } catch (error) {
                            console.error('❌ [PaymentSuccess] Erro ao confirmar pagamento no backend:', error);
                            // Não mudar status para error, pois o pagamento foi confirmado no Stripe
                            // O usuário pode atualizar manualmente depois se necessário
                        }

                        // Aguardar usuário estar logado antes de fechar carrinho
                        console.log('🛒 [PaymentSuccess] Verificando sessão do usuário...');
                        console.log('🔍 [PaymentSuccess] isLoggedIn:', isLoggedIn);
                        console.log('🔍 [PaymentSuccess] user:', user);

                        if (isLoggedIn && user) {
                            console.log('🛒 [PaymentSuccess] Usuário logado, fechando carrinho...');
                            try {
                                await closeCart();
                                console.log('✅ [PaymentSuccess] Carrinho fechado com sucesso');
                            } catch (error) {
                                console.error('❌ [PaymentSuccess] Erro ao fechar carrinho:', error);
                            }
                        } else {
                            console.log('⚠️ [PaymentSuccess] Usuário não logado, marcando fechamento como pendente...');
                            setPendingCartClose(true);
                        }
                    } else {
                        setStatus('error');
                        setMessage('Pagamento não foi confirmado.');
                    }
                } else if (paymentIntentParam) {
                    setPaymentIntentId(paymentIntentParam);
                    const paymentIntent = await StripeService.getPaymentIntent(paymentIntentParam);

                    if (paymentIntent.success && paymentIntent.data.status === 'succeeded') {
                        setStatus('success');
                        setMessage('Pagamento realizado com sucesso!');

                        if (isLoggedIn && user) {

                            try {
                                await closeCart();

                            } catch (error) {
                                console.error('❌ [PaymentSuccess] Erro ao fechar carrinho:', error);
                            }
                        } else {
                            console.log('⚠️ [PaymentSuccess] Usuário não logado, marcando fechamento como pendente...');
                            setPendingCartClose(true);
                        }
                    } else {
                        setStatus('error');
                        setMessage('Pagamento não foi confirmado.');
                    }
                } else {
                    setStatus('error');
                    setMessage('Parâmetros de pagamento não encontrados.');
                }
            } catch (error) {
                console.error('Erro ao verificar status do pagamento:', error);
                setStatus('error');
                setMessage('Erro ao verificar status do pagamento.');
            }
        };

        checkPaymentStatus();
    }, []);

    // Monitorar quando o usuário fica logado para executar fechamento pendente
    useEffect(() => {
        if (pendingCartClose && isLoggedIn && user) {

            setPendingCartClose(false);

            const executeCartClose = async () => {
                try {
                    await closeCart();
                } catch (error) {

                }
            };

            executeCartClose();
        }
    }, [isLoggedIn, user, pendingCartClose, closeCart]);

    const handleContinue = () => {
        setLocation('/');
    };

    const handleViewOrders = () => {
        setLocation('/orders');
    };

    if (status === 'loading') {
        return (
            <Card className={className}>
                <CardContent className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
                    <p className="text-lg font-medium">Verificando pagamento...</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={className}>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    {status === 'success' ? (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                    ) : (
                        <AlertCircle className="h-6 w-6 text-red-600" />
                    )}
                    {status === 'success' ? 'Pagamento Confirmado' : 'Erro no Pagamento'}
                </CardTitle>
                <CardDescription>
                    {status === 'success'
                        ? 'Seu pagamento foi processado com sucesso.'
                        : 'Houve um problema com seu pagamento.'
                    }
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <Alert variant={status === 'success' ? 'default' : 'destructive'}>
                    <AlertDescription>{message}</AlertDescription>
                </Alert>

                {status === 'success' && (
                    <div className="space-y-2">
                        <p className="text-sm text-gray-600">
                            Você receberá um email de confirmação em breve.
                        </p>
                        {sessionId && (
                            <p className="text-xs text-gray-500">
                                ID da Sessão: {sessionId}
                            </p>
                        )}
                        {paymentIntentId && (
                            <p className="text-xs text-gray-500">
                                ID do Pagamento: {paymentIntentId}
                            </p>
                        )}
                    </div>
                )}

                <div className="flex gap-2">
                    <Button onClick={handleContinue} variant="outline">
                        Continuar Navegando
                    </Button>
                    {status === 'success' && (
                        <Button onClick={handleViewOrders}>
                            Ver Meus Pedidos
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
